/**
 * The units on a job, each with its PRESSURE STACK
 * (POST /jobs/:id/units, PATCH / DELETE /job-units/:id,
 * PUT /jobs/:id/pressure-stack — fieldiq-api#1338).
 *
 * THE STACK GOES THROUGH THE SHARED GUARD. Setting a stack runs the
 * pressure-control check for it and every component under it against the
 * job's planned end; a refusal is 409 pressure_stack_refused and is shown
 * here as the API's sentence with each failure on its own line, inline under
 * the unit. Due-soon warnings come back on a success and are shown, never
 * blocking. Clearing a stack sends equipment_id null and runs no check.
 *
 * Every write is WL_DISPATCH; the API decides (unit_has_sheets, already_on_job,
 * unit_sheet_outside_dates, job_closed) and its sentence is shown.
 */
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWlEquipmentOptions, useWlUnits } from '../../hooks/useWirelineSlickline';
import { useAddWlJobUnit, useRemoveWlJobUnit, useSetWlPressureStack } from '../../hooks/useWlJobs';
import { wlDay, wlJobRefusal, wlStackRefusal, wlWarningText } from '../../lib/wireline-slickline-jobs';
import type { WlJobDetail, WlJobUnit, WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlDialog, WlEmpty, WlField, WlFormError, WlPanel, WlWarning } from './WlBits';

function StackRefusal({ refusal }: { refusal: { message: string; failures: string[] } }) {
  return (
    <div role="alert" data-testid="wl-stack-refused" className="rounded-fi-control border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      <p className="font-semibold">{refusal.message}</p>
      {refusal.failures.length > 1 && (
        <ul className="mt-1 list-disc pl-5">
          {refusal.failures.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UnitRow({ jobId, unit, canWrite, open }: { jobId: string; unit: WlJobUnit; canWrite: boolean; open: boolean }) {
  const equipment = useWlEquipmentOptions(canWrite && open);
  const setStack = useSetWlPressureStack();
  const remove = useRemoveWlJobUnit();
  const [stackId, setStackId] = useState('');
  const [refusal, setRefusal] = useState<{ message: string; failures: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WlWarningEntry[]>([]);

  const send = (equipmentId: string | null) => {
    setRefusal(null);
    setError(null);
    setWarnings([]);
    setStack.mutate(
      { jobId, unitId: unit.unitId, equipmentId },
      {
        onSuccess: (res) => {
          toast.success(equipmentId ? 'Pressure stack set' : 'Pressure stack cleared');
          setWarnings(res.warnings);
          setStackId('');
        },
        onError: (err) => {
          const r = wlStackRefusal(err);
          if (r) setRefusal(r);
          else setError(wlJobRefusal(err, 'The pressure stack could not be set.'));
        },
      },
    );
  };

  return (
    <li className="space-y-3 px-4 py-4" data-testid="wl-job-unit">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-extrabold text-white">{unit.unitNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {unit.equipmentName || '—'} · on {wlDay(unit.onDate)} · off {wlDay(unit.offDate)}
          </p>
          <p className="mt-1 text-sm text-fieldiq-text" data-testid="wl-job-unit-stack">
            Pressure stack: <span className="font-semibold text-white">{unit.pressureStackName || '—'}</span>
          </p>
        </div>
        {canWrite && open && (
          <button
            type="button"
            className={WL_SMALL_BTN}
            aria-label={`Remove ${unit.unitNumber || 'unit'} from the job`}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(unit.id, {
                onSuccess: () => toast.success('Unit removed from the job'),
                onError: (err) => setError(wlJobRefusal(err, 'The unit could not be removed.')),
              })
            }
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Remove
          </button>
        )}
      </div>
      {canWrite && open && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <WlField label="Set pressure stack" hint="Checked against the job’s planned end, with every component under it">
              <select className={WL_INPUT} aria-label={`Pressure stack for ${unit.unitNumber || 'unit'}`} value={stackId} onChange={(e) => setStackId(e.target.value)}>
                <option value="">{equipment.isLoading ? 'Loading…' : 'Choose equipment'}</option>
                {(equipment.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                    {e.unitNumber ? ` · ${e.unitNumber}` : ''}
                  </option>
                ))}
              </select>
            </WlField>
          </div>
          <button type="button" className={WL_PRIMARY} disabled={!stackId || setStack.isPending} onClick={() => send(stackId)}>
            {setStack.isPending ? 'Checking…' : 'Set stack'}
          </button>
          {unit.pressureStackEquipmentId && (
            <button type="button" className={WL_SECONDARY} disabled={setStack.isPending} onClick={() => send(null)}>
              Clear stack
            </button>
          )}
        </div>
      )}
      {refusal && <StackRefusal refusal={refusal} />}
      <WlFormError message={error} />
      {warnings.map((w, i) => (
        <WlWarning key={i} message={wlWarningText(w)} testId="wl-stack-warning" />
      ))}
    </li>
  );
}

export function JobUnitsPanel({ job, canWrite, open }: { job: WlJobDetail; canWrite: boolean; open: boolean }) {
  const units = useWlUnits();
  const add = useAddWlJobUnit();
  const [adding, setAdding] = useState(false);
  const [unitId, setUnitId] = useState('');
  const [onDate, setOnDate] = useState('');
  const [offDate, setOffDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const onJob = new Set(job.units.map((u) => u.unitId));
  const choices = (units.data?.data ?? []).filter((u) => !onJob.has(u.id));

  return (
    <WlPanel
      eyebrow="Units"
      title="Units on this job"
      testId="wl-job-units"
      actions={
        canWrite && open ? (
          <button type="button" className={WL_SMALL_BTN} onClick={() => { setError(null); setAdding(true); }}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add unit
          </button>
        ) : undefined
      }
    >
      {job.units.length === 0 ? (
        <WlEmpty>No unit is on this job yet.{canWrite && open ? ' A day sheet is opened for a unit, so add one first.' : ''}</WlEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {job.units.map((u) => (
            <UnitRow key={u.id} jobId={job.id} unit={u} canWrite={canWrite} open={open} />
          ))}
        </ul>
      )}
      <WlDialog
        open={adding}
        title="Add a unit to the job"
        onClose={() => setAdding(false)}
        footer={
          <>
            <button type="button" className={WL_SECONDARY} onClick={() => setAdding(false)} disabled={add.isPending}>
              Cancel
            </button>
            <button
              type="button"
              className={WL_PRIMARY}
              disabled={!unitId || add.isPending}
              onClick={() => {
                setError(null);
                add.mutate(
                  { jobId: job.id, body: { unit_id: unitId, on_date: onDate || null, off_date: offDate || null } },
                  {
                    onSuccess: () => {
                      toast.success('Unit added to the job');
                      setAdding(false);
                      setUnitId('');
                      setOnDate('');
                      setOffDate('');
                    },
                    onError: (err) => setError(wlJobRefusal(err, 'The unit could not be added.')),
                  },
                );
              }}
            >
              {add.isPending ? 'Adding…' : 'Add unit'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <WlFormError message={error} />
          <WlField label="Unit">
            <select className={WL_INPUT} aria-label="Unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              <option value="">{units.isLoading ? 'Loading…' : choices.length ? 'Choose a unit' : 'Every unit is already on this job'}</option>
              {choices.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.unitNumber}
                  {u.equipmentName ? ` · ${u.equipmentName}` : ''}
                </option>
              ))}
            </select>
          </WlField>
          <div className="grid gap-3 sm:grid-cols-2">
            <WlField label="On the job from" hint="Blank: open">
              <input type="date" className={WL_INPUT} aria-label="On the job from" value={onDate} onChange={(e) => setOnDate(e.target.value)} />
            </WlField>
            <WlField label="Off the job" hint="Blank: open">
              <input type="date" className={WL_INPUT} aria-label="Off the job" value={offDate} onChange={(e) => setOffDate(e.target.value)} />
            </WlField>
          </div>
        </div>
      </WlDialog>
    </WlPanel>
  );
}
